import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { PeopleFilters } from './PeopleFilters';
import { PeopleFilters as PeopleFiltersType } from '../../hooks/usePeopleFilters';
import { Person } from '../../types';

/**
 * FilterDrawer Component
 *
 * Mobile-responsive drawer for filtering people
 *
 * Features:
 * - Side drawer (anchor right)
 * - Contains PeopleFilters component
 * - Close button
 * - Clear All Filters button
 * - Mobile-friendly width (90% on mobile, 400px on desktop)
 *
 * @component
 * @param {FilterDrawerProps} props - Component props
 * @returns {JSX.Element} Filter drawer
 */
interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  filters: PeopleFiltersType;
  onFilterChange: (filters: PeopleFiltersType) => void;
  onClearFilters: () => void;
  persons: Person[];
}

export const FilterDrawer: React.FC<FilterDrawerProps> = ({
  open,
  onClose,
  filters,
  onFilterChange,
  onClearFilters,
  persons,
}) => {
  // ===== Event Handlers =====
  const handleClearFilters = () => {
    onClearFilters();
  };

  // ===== Render =====
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '90%', sm: 400 },
          maxWidth: '100%',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon />
            <Typography variant="h6">Filters</Typography>
          </Box>
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Filter Controls */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          <PeopleFilters
            filters={filters}
            onChange={onFilterChange}
            persons={persons}
          />
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            gap: 2,
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={handleClearFilters}
          >
            Clear All Filters
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={onClose}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default FilterDrawer;
